import { AtSign, Bell, Info, MessageSquare, RefreshCw, RotateCw, UserPlus } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import type { MemberDocument } from '@/lib/db/rxdb/schemas/member'
import type { NotificationIconKind } from '../utils/notificationInbox'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { memberAvatarSrc } from '@/lib/utils/memberAvatar'
import { cn } from '@/lib/utils'

const BADGES: Record<NotificationIconKind, { icon: LucideIcon; className: string }> = {
  mention: { icon: AtSign, className: 'bg-brand text-brand-foreground' },
  comment: { icon: MessageSquare, className: 'bg-muted-foreground text-background' },
  assign: { icon: UserPlus, className: 'bg-foreground text-background' },
  reminder: { icon: Bell, className: 'bg-amber-500 text-white dark:bg-amber-400 dark:text-black' },
  cycle: { icon: RotateCw, className: 'bg-brand text-brand-foreground' },
  status: { icon: RefreshCw, className: 'bg-sky-600 text-white dark:bg-sky-400 dark:text-black' },
  system: { icon: Info, className: 'bg-muted-foreground text-background' },
}

function initialsOf(label: string) {
  const [first = '', second = ''] = label.trim().split(/\s+/).filter(Boolean)
  if (!first) return '?'
  if (!second) return first.slice(0, 2).toUpperCase()
  return (first.charAt(0) + second.charAt(0)).toUpperCase()
}

interface NotificationAvatarProps {
  member: MemberDocument | null | undefined
  label: string
  kind: NotificationIconKind
  className?: string
}

/** Actor avatar with a small badge for the notification type (design 17a). */
export function NotificationAvatar({ member, label, kind, className }: NotificationAvatarProps) {
  const badge = BADGES[kind]
  const Icon = badge.icon
  return (
    <span className={cn('relative inline-flex size-7 shrink-0', className)} aria-hidden>
      <Avatar className="size-7 rounded-full">
        <AvatarImage src={memberAvatarSrc(member)} alt="" />
        <AvatarFallback className="rounded-full bg-foreground/10 text-[10px] font-semibold text-foreground/80">
          {initialsOf(member?.name || member?.email || label)}
        </AvatarFallback>
      </Avatar>
      <span
        data-testid={`notification-badge-${kind}`}
        className={cn(
          'absolute -bottom-0.5 -right-0.5 flex size-3.5 items-center justify-center rounded-full ring-2 ring-background',
          badge.className,
        )}
      >
        <Icon className="size-2" strokeWidth={2.75} />
      </span>
    </span>
  )
}
